/**
 * The Atlas's mount on the shared renderer: the pointer surface over its
 * scissor rect, the stage, the subtree it hangs in the one world scene, the
 * camera with its heliocentric position, and the resize that keeps the stage
 * and the camera in step with the pane.
 */

import * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import { createStage, type StageHandle } from '../world/stage.ts';
import { worldScene, type SubtreeHandle } from '../world/scene.ts';
import type { WorldFrameHandle, WorldVec } from '../world/frame.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';

/**
 * The one far plane, scene metres.
 *
 * Big enough to hold the whole system in frame - Pluto is 47 AU out at the
 * mission epoch, and framing it needs about 126 AU of standoff - which is what
 * the logarithmic depth buffer buys: 0.5 mm to 1300 AU in one pass. The cost is
 * relative depth precision of about 2e-6, so two coplanar surfaces a hundredth
 * of a percent of their distance apart will fight, which is why the map
 * overlays below sit kilometres off the datum rather than metres.
 */
const FAR_PLANE = 2e14;
/**
 * The near plane everywhere the scene is metres: half a millimetre.
 *
 * The plot's own frame is metres too, so this one value covers the
 * walkaround as well - nothing sizes it off the eye any more. Only
 * `nearClipInject`, the `--band` control, overrides it directly.
 */
export const SCENE_NEAR = 0.0005;

/** Where the star shell sits: inside the far plane, outside everything drawn. */
export const STAR_SHELL_M = 1e13;

/** The pane's clear colour, which the sky from under the air is blended over. */
export const ATLAS_CLEAR_HEX = 0x05080f;

export interface MountDeps {
  readonly renderer: THREE.WebGLRenderer;
  readonly root: HTMLElement;
  own(fn: () => void): void;
  readonly frameGovernor: FrameGovernorHandle;
  readonly state: AtlasState;
  /** The stage's opening tone mapping: `BAND_EXPOSURE.SYSTEM`, where the eased exposure starts. */
  readonly exposure: number;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands these over through getters. */
  readonly focusRig: FocusRigHandle;
  readonly bodyScene: BodySceneHandle;
}

export interface MountHandle {
  readonly surface: HTMLDivElement;
  readonly stage: StageHandle;
  readonly scene: THREE.Scene;
  readonly subtree: SubtreeHandle;
  readonly camera: THREE.PerspectiveCamera;
  readonly cameraWorld: WorldVec;
  readonly atlasRoot: THREE.Group;
  readonly worldFrame: WorldFrameHandle;
  readonly worldRoot: THREE.Object3D;
  /** The pane's background colour, which the mounted air decides the shade of. */
  readonly space: THREE.Color;
  resize(): void;
}

export function createMount(deps: MountDeps): MountHandle {
  const { renderer, root, own, state, exposure } = deps;
  const { governor, pixelRatio } = deps.frameGovernor;

  /**
   * The stage's pointer surface.
   *
   * A transparent element exactly over the scissor rect. It stands in for the
   * canvas the Atlas used to own - the rect every pick, drag and instrument
   * measures against - which is why it keeps the `atlas-canvas` class.
   */
  const surface = document.createElement('div');
  surface.className = 'atlas-canvas';
  root.appendChild(surface);
  // The pane is a hole onto the shared canvas now, so the root cannot paint
  // over it; menu.ts opens the rest of the menu chrome the same way.
  root.style.background = 'transparent';

  const stage = createStage(renderer, surface, {
    clearColor: ATLAS_CLEAR_HEX,
    toneMappingExposure: exposure,
    shadowMap: { enabled: true, type: THREE.PCFSoftShadowMap, autoUpdate: false },
  });
  own(() => stage.dispose());

  // One scene and one root for the whole page (`src/world/scene.ts`): the
  // Atlas mounts a subtree into the root the sim is already drawing through
  // instead of building a scene of its own per visit, and takes it back out at
  // dispose. The scene-level state rides on the subtree, so the sim's IBL
  // never lights this terrain and this clear colour never reaches the sim.
  const world = worldScene();
  const scene = world.scene;
  const subtree = world.mount('atlas');
  const space = new THREE.Color(ATLAS_CLEAR_HEX);
  subtree.setSceneState({ background: space });
  // At mount, not at the first draw: the mount compiles programs against this
  // scene (`renderer.compile` for the orrery and the tile warm), and a compile
  // that read the sim's lights would warm a variant no Atlas frame draws.
  subtree.activate();
  scene.userData['governor'] = governor;
  own(() => { delete scene.userData['governor']; });
  /**
   * One clip pair for every tier.
   *
   * There used to be a pair per tier - the orrery needs thousands of units of
   * far plane and a plot needs a near plane you can push against the ground -
   * and the swap is what made every altitude a discrete state. The logarithmic
   * depth buffer spends its precision per decade instead, so one pair covers
   * both ends and nothing has to know which band it is in to be drawn.
   */
  const camera = new THREE.PerspectiveCamera(45, 1, SCENE_NEAR, FAR_PLANE);
  subtree.camera = camera;
  own(() => { subtree.camera = null; });

  /** The camera's own heliocentric position, scene metres, written each tick. */
  const cameraWorld: WorldVec = { x: 0, y: 0, z: 0 };
  const atlasRoot = subtree.group;
  const worldFrame = subtree.frame;
  const worldRoot = worldFrame.root;
  // `placeFarField` writes this matrix outright below the SURFACE edge, where
  // the body's own frame is drawn inside the walkaround's; identity everywhere
  // else, which is what the children's own camera-relative positions assume.
  worldRoot.matrixAutoUpdate = false;
  own(() => subtree.dispose());

  function resize(): void {
    if (deps.live.disposed) return;
    const w = root.clientWidth;
    const h = root.clientHeight;
    if (w === 0 || h === 0) return;
    // The canvas is the sim's and window-sized; the stage owns the drawing
    // buffer scale, and the budget is still the STAGE's pixels because those
    // are the only ones the Atlas pays for.
    stage.setPixelRatio(pixelRatio());
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    // The default heliocentric framing is chosen to hold the bodies inside the
    // opening fit's reach, which is a function of the aspect, so it is re-chosen
    // on a resize rather than baked at mount.
    // Only while the view IS the opening one: the fit is sol-centred, so once
    // the rig has been aimed at a body a resize must not take that away - and
    // a wheel spent on Sol leaves the tier and the body where the fit put them,
    // so the POSE is what says the view is still that fit's.
    if (state.tier === 0 && deps.bodyScene.state.orrery
      && (state.bodyId === null || state.bodyId === 'sol') && deps.focusRig.openingFitHeld()) {
      deps.focusRig.fitOpening(w / h);
      deps.live.cameraDirty = true;
    }
    deps.live.needsRender = true;
  }

  return { surface, stage, scene, subtree, camera, cameraWorld, atlasRoot, worldFrame, worldRoot, space, resize };
}
